<template>
  <v-app
    v-if="initialized"
    id="user-application"
  >
    <!-- Bypass Blocks (WCAG 2.4.1): erstes fokussierbares Element der Seite,
         sichtbar erst bei Tastaturfokus. -->
    <a
      class="cc-skip-link"
      href="#main-content"
    >
      {{ t('common.a11y.skipToContent') }}
    </a>
    <v-container
      fluid
      class="navigation-container"
    >
      <v-container>
        <v-navigation-drawer
          app
          fixed
          temporary
          v-model="showSideNav"
        >
          <v-img
            class="h-max-xl-125"
            :src="logoUrl()"
            :alt="t('common.a11y.logo')"
          />
          <v-list nav>
            <template
              v-for="(item, i) in items"
              :key="i"
            >
              <v-list-group v-if="item.hasOwnProperty('items')">
                <template #activator="{props}">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item>
                </template>
                <v-list-item
                  v-for="(subItem, subI) in item.items"
                  :key="subI"
                  :to="{name: subItem.path}"
                  exact
                >
                  <template #prepend>
                    <v-icon :icon="subItem.icon" />
                  </template>
                  <v-list-item-title>
                    {{ subItem.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list-group>
              <v-list-item
                v-else
                :to="{name:item.path}"
                exact
              >
                <template #prepend>
                  <v-icon :icon="item.icon" />
                </template>
                <v-list-item-title>
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item
              tag="a"
              @click="logout()"
              class="d-flex d-md-none mt-5"
            >
              <template #prepend>
                <v-icon :icon="Icons.LOGOUT" />
              </template>
              <v-list-item-title>
                {{ t('navigation.logout') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-navigation-drawer>
        <v-app-bar
          app
          :height="96"
        >
          <!-- T00B0YULKJEE — fixed height instead of width classes that were defined
               nowhere: a tall logo grew to the full width and was cut off by the bar. -->
          <router-link
            class="cc-user-logo mr-5"
            :to="{name: 'userDashboard'}"
          >
            <img
              class="cc-user-logo__img"
              :src="logoUrl()"
              :alt="t('common.a11y.logo')"
            >
          </router-link>
          <template
            v-for="(item, i) in items"
            :key="i"
          >
            <v-menu
              v-if="item.hasOwnProperty('items')"
              nudge-bottom="30"
            >
              <template #activator="{props}">
                <div
                  v-bind="props"
                  class="pr-5 menu-dropdown d-none d-lg-block"
                  :class="{'menu-dropdown--active': isActive(item)}"
                >
                  <span>{{ item.title }}</span>
                  <v-icon :icon="Icons.CHEVRON_DOWN" />
                </div>
              </template>
              <v-list>
                <v-list-item
                  v-for="(subItem, subIndex) in item.items"
                  :key="subIndex"
                  :to="{name: subItem.path}"
                >
                  <v-list-item-title>
                    {{ subItem.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <router-link
              class="pr-5 d-none d-lg-block"
              :class="{'nav-link--active': isActive(item)}"
              v-else
              :to="{name:item.path}"
            >
              <span>{{ item.title }}</span>
            </router-link>
          </template>
          <v-spacer />
          <user-switch-modules />
          <v-tooltip location="bottom">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                class="mr-7 second-menu-item"
                v-if="store.userData?.user_switch_name !== undefined"
                @click="returnUser()"
                :icon="Icons.ACCOUNT_SWITCH"
                :aria-label="t('navigation.tooltip.userSwitch', {name: store.userData?.user_switch_name})"
                @keydown.enter="returnUser()"
                @keydown.space.prevent="returnUser()"
              />
            </template>
            <span>{{ t('navigation.tooltip.userSwitch', {name: store.userData?.user_switch_name}) }}</span>
          </v-tooltip>
          <menu-partner-shops v-if="store.coaching.partnerShops" />
          <menu-calendar v-if="store.coaching.liveCoaching" />
          <menu-notifications />
          <v-menu offset-y>
            <template #activator="{props}">
              <v-icon
                v-bind="props"
                size="large"
                class="d-none d-lg-block second-menu-item mr-3"
                :icon="Icons.ACCOUNT_CIRCLE"
              />
            </template>
            <v-list>
              <v-list-item :to="{name: 'userProfile'}">
                <template #prepend>
                  <v-icon :icon="Icons.ACCOUNT_CIRCLE" />
                </template>
                <v-list-item-title>
                  {{ t('navigation.profile') }}
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item
                tag="a"
                @click="logout()"
              >
                <template #prepend>
                  <v-icon :icon="Icons.LOGOUT" />
                </template>
                <v-list-item-title>
                  {{ t('navigation.logout') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-icon
            class="d-block d-lg-none mr-3"
            :aria-label="t('common.a11y.openProfileMenu')"
            @click="showSideNav = !showSideNav"
            @keydown.enter="showSideNav = !showSideNav"
            @keydown.space.prevent="showSideNav = !showSideNav"
            size="large"
            :icon="Icons.ACCOUNT_CIRCLE"
          />
        </v-app-bar>
      </v-container>
    </v-container>
    <v-main id="main-content">
      <router-view />
    </v-main>
    <!-- T009OSKBRBOH — a running 1:1 call stays when the page changes. -->
    <coaching-call-host />
    <v-footer
      color="primary"
      absolute
      data-v-main-step="4"
    >
      <v-card
        color="primary"
        width="100%"
        flat
      >
        <v-card-text
          class="text-center"
          v-if="promoVisible"
        >
          <h3 class="text-h6 mb-3">
            {{ t('appPromo.footer.headline') }}
          </h3>
          <cc-app-store-links
            size="small"
            color="white"
          />
        </v-card-text>
        <v-card-text
          class="text-center"
          v-if="socialMedias.length > 0"
        >
          <v-btn
            variant="text"
            size="large"
            :href="socialMedia.url"
            target="_blank"
            v-for="socialMedia in socialMedias"
            :key="socialMedia.id"
            :icon="Icons[socialMedia.icon.toUpperCase() as keyof typeof Icons]"
            :aria-label="socialMedia.icon"
          />
        </v-card-text>
        <v-card-text
          class="text-center"
          v-if="footerLinks.length > 0"
        >
          <a
            :href="footerLink.url"
            target="_blank"
            :class="`text-h6 ${footerLinks.length > 1 ? 'mr-10' : ''}`"
            v-for="footerLink in footerLinks"
            :key="footerLink.id"
            v-dompurify-html="footerLink.name"
          />
        </v-card-text>
      </v-card>
    </v-footer>
    <cc-app-promo-dialog />

    <!-- T00B8BPCQBCH — Wirtskomponente für Dialoge und Toasts aus
         client-alerts. Muss innerhalb von <v-app> stehen, damit Theme,
         Overlay-Stapel und Scroll-Sperre von Vuetify greifen. -->
    <alerts-host />
  </v-app>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {SecUtil} from '@/common/utils/security/SecUtil';
import {Icons} from '@simpleforyou/client-core';
//================= [VUE COMPONENTS] ==========================
import CoachingCallHost from '@/components/individualCoaching/call/CoachingCallHost.vue';
import {footerLinks, logoUrl, returnUser, socialMedias} from '@/composables/layout/baseLayout';
import {initialized} from '@/composables/layout/layoutHandler';
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import CcAppPromoDialog from '@/components/common/CcAppPromoDialog.vue';
import CcAppStoreLinks from '@/components/common/CcAppStoreLinks.vue';
import MenuCalendar from '@/layout/menu/MenuCalendar.vue';
import MenuNotifications from '@/layout/menu/MenuNotifications.vue';
import MenuPartnerShops from '@/layout/menu/MenuPartnerShops.vue';
import UserSwitchModules from '@/components/admin/UserSwitchModules.vue';
import {useStore} from '@/common/store/store';
import {useAuth} from '@/composables/useAuth';
import {useAppPromo} from '@/composables/useAppPromo';
const {logout} = useAuth();

const {t} = useI18n();
const route = useRoute();
const store = useStore();
const {promoVisible} = useAppPromo();

// Highlights a nav item when the current route's meta.active matches the item's
// active key. Used for both direct links and the dropdown so detail routes
// (e.g. /tagebuch/:id, /mein-modul/:id) keep their parent entry active.
const isActive = (item: {active?: string | string[]}): boolean => {
  const active = route.meta.active as string | undefined;
  if (active === undefined || item.active === undefined) {
    return false;
  }
  return Array.isArray(item.active) ? item.active.includes(active) : item.active === active;
};
const showSideNav = ref(false);

const items = computed(() => {
  const navigation = [];

  navigation.push({
    title: t('navigation.userDashboard'),
    path: 'userDashboard',
    icon: Icons.BALLOT,
    active: 'userDashboard',
  });
  navigation.push({
    title: t('navigation.userModules'),
    path: 'userModules',
    icon: Icons.BALLOT,
    active: 'userModule',
  });
  // E00U6EKA5EGC — the API decides whether this client is enabled; the page says so if not.
  if (SecUtil.coaching('individualCoaching')) {
    navigation.push({
      title: t('navigation.individualCoachingMine'),
      path: 'individualCoachingMine',
      icon: Icons.ACCOUNT_GROUP,
      active: 'individualCoaching',
    });
  }
  if (SecUtil.coaching('diary')) {
    navigation.push({title: t('navigation.diary'), path: 'diary', icon: Icons.NOTEBOOK, active: 'diary'});
  }

  if (SecUtil.coaching('recipe')) {
    navigation.push({
      title: t('navigation.recipe.label'), icon: Icons.CHEF_HAT,
      active: ['recipe', 'cookbook'],
      items: [
        {title: t('navigation.recipe.recipeList'), path: 'recipeList', icon: Icons.CHEF_HAT, active: 'recipe'},
        {
          title: t('navigation.recipe.cookBook'),
          path: 'cookbook',
          icon: Icons.BOOK_OPEN_VARIANT,
          active: 'cookbook',
        },
      ],
    });
  }
  return navigation;
});

</script>

<style lang="scss">
@use "sass:meta";

@include meta.load-css("@/assets/scss/user.scss");
</style>
